import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "active_project_id";

const ProjectContext = createContext(null);

/**
 * ProjectProvider — gère le projet actif (filtre global) et la liste des projets.
 * Le projet actif est persisté dans localStorage pour que le filtre survive
 * aux rechargements. Si l'utilisateur n'a aucun projet, le sélecteur est masqué.
 */
export function ProjectProvider({ children, user }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveIdState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  const setActiveId = useCallback((id) => {
    setActiveIdState(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/projects`);
      const items = data?.items || [];
      setProjects(items);
      // Functional update so we don't depend on activeId in the deps array
      // (prevents an infinite refresh loop when the active project gets
      // wiped because it's no longer visible to the user).
      setActiveIdState((prev) => {
        if (prev && !items.some((p) => p.id === prev)) {
          try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
          return null;
        }
        return prev;
      });
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [user?.email]);

  useEffect(() => { refresh(); }, [refresh]);

  const active = projects.find((p) => p.id === activeId) || null;

  return (
    <ProjectContext.Provider value={{ projects, active, activeId, setActiveId, refresh, loading }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useActiveProject() {
  return useContext(ProjectContext) || { projects: [], active: null, activeId: null, setActiveId: () => {}, refresh: () => {}, loading: false };
}
